import { ProvideAuth } from 'module/auth/context/AuthContext';
import React from 'react';
import { createRoot } from 'react-dom/client'; // ✅ React 18 method
import App from './App';
import reportWebVitals from './reportWebVitals';
import { clearOldCacheEntriesByCount } from 'core/utils/localStorageUtils';

// react query setup
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';

// import styles
import { ToastContextProvider } from 'core/components/Toast/context/ToastContext';
import { WorkplacesContextProvider } from 'module/workplaces/context/WorkplacesContext';
import 'rsuite/dist/rsuite.min.css';
import './index.scss';
import './styles/chat.scss';

import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';

import { ThemeProvider } from '@mui/material';
import { theme } from 'styles/mui/theme';

// OPS-003: suppress only the known-harmless ResizeObserver loop quirk.
// Do not swallow unrelated console or window errors.
const isResizeObserverLoopError = (value: unknown): boolean => {
	if (typeof value !== 'string') return false;
	return (
		/ResizeObserver loop limit exceeded/i.test(value) ||
		/ResizeObserver loop completed with undelivered notifications/i.test(value)
	);
};

const originalConsoleError = window.console.error.bind(window.console);
window.console.error = (...args: unknown[]) => {
	if (args.length > 0 && isResizeObserverLoopError(args[0])) {
		return;
	}
	originalConsoleError(...args);
};

window.addEventListener('error', (event) => {
	if (isResizeObserverLoopError(event.message)) {
		event.stopImmediatePropagation();
		event.preventDefault();
	}
});

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
			suspense: false,
		},
	},
});

// OPS-001: initialise Sentry only when a DSN is configured for this build.
const sentryDsn = process.env.REACT_APP_SENTRY_DSN?.trim();
if (sentryDsn) {
	Sentry.init({
		dsn: sentryDsn,
		environment: process.env.REACT_APP_SENTRY_ENV || process.env.NODE_ENV,
		integrations: [new Integrations.BrowserTracing()],
		tracesSampleRate: 0.2,
	});
}

// ✅ React 18 root rendering
const container = document.getElementById('root');
if (container) {
	// Clear old cache entries on app startup to prevent quota issues
	try {
		const cachePrefixes = [
			'tv_package_channels_cache',
			'preference_channels_cache',
			'gracenote_lineups_cache',
		];
		
		cachePrefixes.forEach(prefix => {
			clearOldCacheEntriesByCount(prefix, 3); // Keep only 3 most recent entries per prefix
		});
	} catch (e) {
		console.warn('Failed to clear old cache on startup:', e);
	}

	const root = createRoot(container);
	
	// Wrapper component to conditionally apply StrictMode only in development
	const AppWrapper = () => (
		<ThemeProvider theme={theme}>
			<ToastContextProvider>
				<ProvideAuth>
					<QueryClientProvider client={queryClient}>
						<WorkplacesContextProvider>
							<App />
							<ReactQueryDevtools initialIsOpen={false} />
						</WorkplacesContextProvider>
					</QueryClientProvider>
				</ProvideAuth>
			</ToastContextProvider>
		</ThemeProvider>
	);
	
	root.render(
		process.env.NODE_ENV === 'development' ? (
			<React.StrictMode>
				<AppWrapper />
			</React.StrictMode>
		) : (
			<AppWrapper />
		)
	);
}

reportWebVitals();